import { QRDesign } from '@/types/qr';

export interface TemplatePreset {
  id: string;
  name: string;
  category: 'Modern' | 'Gradient' | 'Business' | 'Creative' | 'Dark';
  description: string;
  design: QRDesign;
}

export const TEMPLATE_PRESETS: TemplatePreset[] = [
  {
    id: 'classic',
    name: 'Classic Pro',
    category: 'Modern',
    description: 'Crisp black modules, high contrast, universally readable.',
    design: {
      dotsType: 'square',
      dotsColor: '#0f172a',
      cornersSquareType: 'square',
      cornersSquareColor: '#0f172a',
      cornersDotType: 'square',
      cornersDotColor: '#0f172a',
      backgroundColor: '#ffffff',
      isTransparent: false,
      margin: 12,
      errorCorrection: 'M',
      frame: { type: 'none', text: '', textColor: '', backgroundColor: '' }
    }
  },
  {
    id: 'minimal-rounded',
    name: 'Soft Rounded',
    category: 'Modern',
    description: 'Sleek rounded modules with matching finder patterns.',
    design: {
      dotsType: 'rounded',
      dotsColor: '#1e293b',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#0f172a',
      cornersDotType: 'dot',
      cornersDotColor: '#0f172a',
      backgroundColor: '#f8fafc',
      isTransparent: false,
      margin: 16,
      errorCorrection: 'Q',
      frame: { type: 'none', text: '', textColor: '', backgroundColor: '' }
    }
  },
  {
    id: 'ocean-gradient',
    name: 'Ocean Breeze',
    category: 'Gradient',
    description: 'Smooth gradient transitioning from royal blue to vivid cyan.',
    design: {
      dotsType: 'classy',
      dotsColor: '#2563eb',
      dotsGradient: {
        type: 'linear',
        rotation: 45,
        colorStops: [
          { offset: 0, color: '#1d4ed8' },
          { offset: 1, color: '#06b6d4' }
        ]
      },
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#1e40af',
      cornersDotType: 'dot',
      cornersDotColor: '#0284c7',
      backgroundColor: '#ffffff',
      isTransparent: false,
      margin: 16,
      errorCorrection: 'H',
      frame: { type: 'none', text: '', textColor: '', backgroundColor: '' }
    }
  },
  {
    id: 'sunset-glow',
    name: 'Sunset Glow',
    category: 'Gradient',
    description: 'Warm, energetic gradient blending rich magenta into bright tangerine.',
    design: {
      dotsType: 'dots',
      dotsColor: '#e11d48',
      dotsGradient: {
        type: 'linear',
        rotation: 45,
        colorStops: [
          { offset: 0, color: '#e11d48' },
          { offset: 1, color: '#f97316' }
        ]
      },
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#be123c',
      cornersDotType: 'dot',
      cornersDotColor: '#c2410c',
      backgroundColor: '#ffffff',
      isTransparent: false,
      margin: 16,
      errorCorrection: 'H',
      frame: { type: 'none', text: '', textColor: '', backgroundColor: '' }
    }
  },
  {
    id: 'cyber-neon',
    name: 'Cyber Neon',
    category: 'Dark',
    description: 'Electric violet modules framed with neon emerald accents.',
    design: {
      dotsType: 'classy-rounded',
      dotsColor: '#8b5cf6',
      dotsGradient: {
        type: 'linear',
        rotation: 135,
        colorStops: [
          { offset: 0, color: '#a855f7' },
          { offset: 1, color: '#06b6d4' }
        ]
      },
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#7c3aed',
      cornersDotType: 'dot',
      cornersDotColor: '#10b981',
      backgroundColor: '#090d16',
      isTransparent: false,
      margin: 18,
      errorCorrection: 'H',
      frame: { type: 'none', text: '', textColor: '', backgroundColor: '' }
    }
  },
  {
    id: 'corporate-badge',
    name: 'Corporate Card',
    category: 'Business',
    description: 'Deep navy modules paired with an authoritative SCAN ME pill badge.',
    design: {
      dotsType: 'square',
      dotsColor: '#0f172a',
      cornersSquareType: 'square',
      cornersSquareColor: '#1e3a8a',
      cornersDotType: 'square',
      cornersDotColor: '#1e3a8a',
      backgroundColor: '#ffffff',
      isTransparent: false,
      margin: 12,
      errorCorrection: 'H',
      frame: {
        type: 'bottom-badge',
        text: 'SCAN FOR INFO',
        textColor: '#1e3a8a',
        backgroundColor: '#ffffff'
      }
    }
  },
  {
    id: 'restaurant-menu',
    name: 'Restaurant Menu',
    category: 'Business',
    description: 'Warm espresso modules on Polaroid card styled for table tents.',
    design: {
      dotsType: 'rounded',
      dotsColor: '#3d1a04',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#78350f',
      cornersDotType: 'dot',
      cornersDotColor: '#b45309',
      backgroundColor: '#fffbeb',
      isTransparent: false,
      margin: 14,
      errorCorrection: 'H',
      frame: {
        type: 'card-polaroid',
        text: 'VIEW OUR MENU',
        subtext: 'Scan with your phone camera',
        textColor: '#451a03',
        backgroundColor: '#fef3c7'
      }
    }
  },
  {
    id: 'payment-upi',
    name: 'Instant Payment',
    category: 'Business',
    description: 'Vibrant UPI purple with high-visibility SCAN TO PAY frame.',
    design: {
      dotsType: 'classy',
      dotsColor: '#5b21b6',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#4c1d95',
      cornersDotType: 'dot',
      cornersDotColor: '#6d28d9',
      backgroundColor: '#faf5ff',
      isTransparent: false,
      margin: 12,
      errorCorrection: 'H',
      frame: {
        type: 'bottom-badge',
        text: 'SCAN TO PAY',
        textColor: '#6d28d9',
        backgroundColor: '#ffffff'
      }
    }
  },
  {
    id: 'wifi-connect',
    name: 'Wi-Fi Guest Pass',
    category: 'Business',
    description: 'Teal & Emerald palette with JOIN WI-FI badge.',
    design: {
      dotsType: 'rounded',
      dotsColor: '#0f766e',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#115e59',
      cornersDotType: 'dot',
      cornersDotColor: '#0d9488',
      backgroundColor: '#f0fdfa',
      isTransparent: false,
      margin: 14,
      errorCorrection: 'H',
      frame: {
        type: 'bottom-badge',
        text: 'CONNECT WI-FI',
        textColor: '#0f766e',
        backgroundColor: '#ffffff'
      }
    }
  },
  {
    id: 'pure-dots',
    name: 'Pure Dot Matrix',
    category: 'Creative',
    description: 'Ultra-modern circular modules for an avant-garde look.',
    design: {
      dotsType: 'dots',
      dotsColor: '#334155',
      cornersSquareType: 'dot',
      cornersSquareColor: '#1e293b',
      cornersDotType: 'dot',
      cornersDotColor: '#0f172a',
      backgroundColor: '#ffffff',
      isTransparent: false,
      margin: 16,
      errorCorrection: 'M',
      frame: { type: 'none', text: '', textColor: '', backgroundColor: '' }
    }
  },
  {
    id: 'midnight-stealth',
    name: 'Midnight Stealth',
    category: 'Dark',
    description: 'Pure platinum modules against rich obsidian dark mode surface.',
    design: {
      dotsType: 'rounded',
      dotsColor: '#f1f5f9',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#e2e8f0',
      cornersDotType: 'dot',
      cornersDotColor: '#38bdf8',
      backgroundColor: '#020617',
      isTransparent: false,
      margin: 16,
      errorCorrection: 'H',
      frame: { type: 'none', text: '', textColor: '', backgroundColor: '' }
    }
  },
  {
    id: 'emerald-eco',
    name: 'Forest Emerald',
    category: 'Modern',
    description: 'Lush botanical green with classy modules.',
    design: {
      dotsType: 'classy',
      dotsColor: '#065f46',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#047857',
      cornersDotType: 'dot',
      cornersDotColor: '#059669',
      backgroundColor: '#ecfdf5',
      isTransparent: false,
      margin: 14,
      errorCorrection: 'Q',
      frame: { type: 'none', text: '', textColor: '', backgroundColor: '' }
    }
  }
];
